import { ColumnsType } from 'antd/lib/table';
import { format, parseISO } from 'date-fns';
import { Table } from 'fave-ui';
import { useState } from 'react';
import { defaultTableConfig } from '../../../helpers/defaults';
import {
  McProductDetailsType,
  useFetchMcProductList,
} from '../../../services/MySubmissions';
import { TableSettingsType } from '../../../types/configTypes';
import {
  copyToClipboard,
  createOnTableChangeHandler,
  downloadFileFromURL,
  formatFilter,
  scrollTableConfig,
} from '../../../utils/utilFunctions';
import { RenderColumn } from '../../../helpers/tableHelpers/RenderColumn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Funnel } from 'phosphor-react';
import { mySubmissionElementIDs } from '../elementId';
import { statusFilter } from '../helpers';
import { MySubmissionStatusType } from '../../../types/dataTypes';
import { Button, Dropdown, Menu } from 'antd';

const scrollConfig = scrollTableConfig();

export const StampCardTab: React.FC = () => {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('query');
  const [tableConfig, setTableConfig] =
    useState<TableSettingsType>(defaultTableConfig);

  const tableChangeHandler = createOnTableChangeHandler(
    setTableConfig,
    filters =>
      formatFilter([
        {
          key: 'mc_my_submission_status',
          value: filters.mc_my_submission_status,
        },
      ]),
  );

  const { data: stampCardData, isFetching } = useFetchMcProductList({
    page: tableConfig.current,
    filter: `product_type=LoyaltySetting|mc_my_submission_status=${
      tableConfig.filter?.split('mc_my_submission_status=')[1] ||
      'all my submissions'
    }|partner=${keyword || ''}`,
  });

  const tabColumns: ColumnsType<McProductDetailsType> = [
    {
      title: 'Submission Date',
      dataIndex: 'bd_submit_time',
      width: 200,
      render: (date: string) =>
        date ? format(parseISO(date), 'd MMM yyyy') : '-',
    },
    {
      title: 'Submitted By',
      dataIndex: ['bd', 'name'],
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Merchant Name',
      dataIndex: ['company', 'name'],
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Minimum Amount',
      dataIndex: ['product_details', 'min_spending_amount_humanize'],
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Maximum Stamp',
      dataIndex: ['product_details', 'total_stamps_count'],
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Duration',
      dataIndex: ['product_details', 'duration'],
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Start Date',
      dataIndex: ['product_details', 'start_date'],
      width: 200,
      render: (date: string) =>
        date ? format(parseISO(date), 'd MMM yyyy') : '-',
    },
    {
      title: 'End Date',
      dataIndex: ['product_details', 'end_date'],
      width: 200,
      render: (date: string) =>
        date ? format(parseISO(date), 'd MMM yyyy') : '-',
    },
    {
      title: 'Submission Status',
      dataIndex: 'mc_my_submission_status',
      width: 200,
      filterIcon: (
        <Funnel
          size={16}
          weight="light"
          data-id={mySubmissionElementIDs.stampCardSubmissionStatusFilterIcon}
        />
      ),
      filters: statusFilter,
      filterMode: 'tree',
      render: (status: MySubmissionStatusType, data) =>
        RenderColumn.mySubmissionsStatusPopover(status, data, 'McProduct'),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_, data) => <ActionItems data={data} />,
      fixed: 'right',
    },
  ];

  return (
    <Table
      dataSource={stampCardData?.mc_products}
      columns={tabColumns}
      scroll={scrollConfig}
      loading={isFetching}
      onChange={tableChangeHandler}
      pagination={{
        total: stampCardData?.count,
        position: ['bottomCenter'],
        pageSize: tableConfig.pageSize,
        current: tableConfig.page,
        showSizeChanger: false,
        hideOnSinglePage: true,
      }}
      rowKey={row => row.id}
    />
  );
};

const ActionItems = ({ data }: { data: McProductDetailsType }) => {
  const navigate = useNavigate();

  const viewAction = {
    label: <span data-id={mySubmissionElementIDs.stampCardViewBtn}>View</span>,
    onClick: () =>
      navigate(
        `/admin/merchant_connect/my_submissions/loyalty_settings/${data.id}`,
      ),
  };

  const editAndResubmitAction = {
    label: (
      <span data-id={mySubmissionElementIDs.stampCardEditResubmitBtn}>
        {'Edit & Resubmit'}
      </span>
    ),
    onClick: () =>
      navigate(
        `/admin/merchant_connect/merchants/${data.company.id}/loyalty_settings/${data.id}/edit`,
      ),
  };

  const copyLinkAction = (link: string) => ({
    label: (
      <span data-id={mySubmissionElementIDs.stampCardCopyLinkBtn}>
        Copy Link
      </span>
    ),
    onClick: () => copyToClipboard(link),
  });

  const downloadContractAction = (url: string) => ({
    label: (
      <span data-id={mySubmissionElementIDs.stampCardDownloadContractBtn}>
        Download Contract
      </span>
    ),
    onClick: () => downloadFileFromURL(url),
  });

  const items = [viewAction];

  const addEditAndResubmitToActions = () => {
    items.push(editAndResubmitAction);
  };

  const addCopyLinkToActions = (link: string) => {
    items.push(copyLinkAction(link));
  };

  const addDownloadContractToActions = (url: string) => {
    items.push(downloadContractAction(url));
  };

  switch (data.mc_my_submission_status) {
    case 'Pending signature':
    case 'Pending Ack.':
      if (data.can_manage_submission) addEditAndResubmitToActions();
      if (data.addendum_link) addCopyLinkToActions(data.addendum_link);
      break;
    case 'Signed':
    case 'Completed':
    case 'Cancelled':
      if (
        data.mc_my_submission_status === 'Cancelled' &&
        data.can_manage_submission
      ) {
        addEditAndResubmitToActions();
      }
      if (data.contract_url) addDownloadContractToActions(data.contract_url);
      break;
  }

  const menuItems = items.map((item, index) => ({ ...item, key: index }));
  const menu = <Menu items={menuItems} />;

  return (
    <Dropdown overlay={menu} trigger={['hover']} placement={'bottomLeft'}>
      <Button
        type={'text'}
        data-id={mySubmissionElementIDs.stampCardActionBtn}
        onClick={e => e.preventDefault()}
      >
        {'. . .'}
      </Button>
    </Dropdown>
  );
};
